import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { PageContainer } from '@/components/ui/page-container';
import { useQuoteRequests } from '../api/hooks';
import type { QuoteRequest, QuoteRequestStatus } from '../api/types';
import { RequestDetailDrawer } from './RequestDetailDrawer';

interface RequestsViewProps {
  readonly getAccessToken: () => Promise<string>;
}

function requestStatusBadgeProps(status: QuoteRequestStatus): {
  className: string;
} {
  switch (status) {
    case 'new':
      return { className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' };
    case 'viewed':
      return { className: 'bg-secondary text-secondary-foreground' };
    case 'quoted':
      return { className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' };
  }
}

function formatRelativeDate(dateStr: string, locale: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

  if (diffHours < 1) return 'Just now';
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffHours < 48) return 'Yesterday';
  return date.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
}

export function RequestsView({ getAccessToken }: RequestsViewProps) {
  const { t, i18n } = useTranslation();
  const [selectedRequest, setSelectedRequest] = useState<QuoteRequest | null>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useQuoteRequests(getAccessToken);

  useEffect(() => {
    if (!sentinelRef.current || !hasNextPage) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const allRequests = data?.pages.flatMap((page) => page.quote_requests) ?? [];
  const newCount = data?.pages[0]?.new_count ?? 0;

  const handleUpdate = () => {
    setSelectedRequest(null);
    refetch();
  };

  return (
    <PageContainer>
      {/* Header */}
      <div className="mb-4 flex items-center gap-2">
        <h2 className="text-xl font-semibold">{t('requests.title')}</h2>
        {newCount > 0 && (
          <Badge
            className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
            data-testid="new-count-badge"
          >
            {newCount}
          </Badge>
        )}
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              data-testid="request-skeleton"
              className="h-16 animate-pulse rounded-lg bg-muted"
            />
          ))}
        </div>
      )}

      {/* Error */}
      {isError && (
        <div className="py-8 text-center">
          <p className="text-sm text-muted-foreground">{t('error.loadQuotesFailed')}</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-2 text-sm text-primary underline"
          >
            {t('quotes.retry')}
          </button>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !isError && allRequests.length === 0 && (
        <div className="py-12 text-center">
          <p className="text-sm font-medium text-muted-foreground">{t('requests.empty')}</p>
          <p className="mt-1 text-xs text-muted-foreground">{t('requests.emptyDetail')}</p>
        </div>
      )}

      {/* Request list */}
      {allRequests.length > 0 && (
        <div className="space-y-2">
          {allRequests.map((request) => (
            <button
              key={request.id}
              type="button"
              data-testid="request-row"
              className="w-full rounded-lg border border-border bg-card p-4 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => setSelectedRequest(request)}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{request.customer_name}</p>
                  <p className="mt-0.5 truncate text-sm text-muted-foreground">
                    {request.project_description}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <Badge {...requestStatusBadgeProps(request.status)}>
                    {t(`requests.${request.status}`)}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {formatRelativeDate(request.created_at, i18n.language)}
                  </span>
                </div>
              </div>
            </button>
          ))}

          {hasNextPage && <div ref={sentinelRef} className="h-1" data-testid="scroll-sentinel" />}
          {isFetchingNextPage && (
            <div className="flex justify-center py-2">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}
        </div>
      )}

      {/* Detail drawer */}
      {selectedRequest && (
        <RequestDetailDrawer
          request={selectedRequest}
          getAccessToken={getAccessToken}
          onClose={() => setSelectedRequest(null)}
          onUpdate={handleUpdate}
        />
      )}
    </PageContainer>
  );
}
